<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { TextBox as KTextBox } from '@progress/kendo-vue-inputs'
import type { TextBoxChangeEvent } from '@progress/kendo-vue-inputs'
import { searchIcon } from '@progress/kendo-svg-icons'
import { ref, onMounted } from 'vue'
import { Popup } from '@progress/kendo-vue-popup'
import { GetSkuListBySearchWord } from '../api/Sku'
import { useLogger } from 'vue-logger-plugin'
import { FormatCurrency } from '../services/currencyFormatter'
import type {
  IGetSkuListBySearchWordResponse,
  IGetSkuListBySearchWordResponseItem,
} from '../types/SkuTypes'
import {
  GetFullProductImageUrl,
  GetFullProductUrl,
} from '../services/SkuService'
import AoeAddToCartButton from '../components/AoeAddToCartButton.vue'
import AoeAddToWishlistButton from '../components/AoeWishlistImageButton.vue'
import PricePerUnitWeight from '../components/PricePerUnitWeight.vue'
import {
  GetOrderTypeId
} from '../stores/SettingsStore'
import AoeProductReviewStars from './AoeProductReviewStars.vue'
import { GetAvailableQty } from '../services/InventoryService'

const logger = useLogger()

const show = ref(false)
const searchKeyword = ref('')
const orderTypeId = ref<string>('')

const moreSearchResultsMessage = ref<string | null>(null)

interface iViewModel extends IGetSkuListBySearchWordResponseItem {
  fullProductUrl: string
  fullProductImageUrl: string
  calculatedAvailableQty: number
}

const viewModels = ref<Array<iViewModel>>([])

async function UpdateViewModels(
  response: IGetSkuListBySearchWordResponse | null,
) {
  if (response && response.totalCount > 0) {
    viewModels.value = await Promise.all(
      response.items.map(async item => {
        const fullProductUrl = await GetFullProductUrl(
          item.categoryCode,
          item.product.productCode,
        )
        const fullProductImageUrl = await GetFullProductImageUrl(
          item.product.productCode,
          item.product.attributes.CatalogImage,
        )
        // Calculate available quantity using the InventoryService
        const calculatedAvailableQty = await GetAvailableQty(orderTypeId.value, item.skuCode)
        logger.debug('Calculated Available Qty for', item.skuCode, ':', calculatedAvailableQty)
        return {
          ...item,
          fullProductUrl,
          fullProductImageUrl,
          calculatedAvailableQty,
        }
      }),
    )
    if (response.totalCount > 4) {
      moreSearchResultsMessage.value = t('labels.ShowMore') + ` (${response.totalCount})`
    }
  } else {
    moreSearchResultsMessage.value = null
    viewModels.value = []
  }
}

function searchClickedHandler(event: MouseEvent) {
  logger.debug('searchClickedHandler', event)
  if (!searchKeyword.value || searchKeyword.value.length === 0) {
    UpdateViewModels(null)
    return
  }

  show.value = !show.value
  if (!show.value) {
    UpdateViewModels(null)
  }

  GetSkuListBySearchWord(orderTypeId.value, searchKeyword.value, 4).then(
    response => {
      logger.debug(
        'searchClickedHandler',
        orderTypeId.value,
        searchKeyword.value,
        response,
      )
      UpdateViewModels(response)
    },
  )
}

function showMoreSearchClickedHandler(event: MouseEvent) {
  logger.debug('showMoreSearchClickedHandler', event)
  if (!searchKeyword.value || searchKeyword.value.length === 0) {
    UpdateViewModels(null)
    return
  }
  GetSkuListBySearchWord(orderTypeId.value, searchKeyword.value, 99).then(
    response => {
      logger.debug(
        'searchClickedHandler',
        orderTypeId.value,
        searchKeyword.value,
        response,
      )
      UpdateViewModels(response)
      moreSearchResultsMessage.value = null
    },
  )
}

function searchKeywordChangedHandler(event: TextBoxChangeEvent) {
  logger.debug('searchKeywordChangedHandler', event)
  searchKeyword.value = event.value as string
}

function searchKeywordInputHandler(event: TextBoxChangeEvent) {
  logger.debug('searchKeywordInputHandler', event)
  searchKeyword.value = event.value as string
}

onMounted(async () => {
  logger.debug('onMounted')

  orderTypeId.value = await GetOrderTypeId()
})

</script>
<template>
  <div class="aoe-search-bar" data-testid="search-bar-container">
    <div class="row searchBarRow" data-testid="search-bar-row">
      <div class="col-12 searchBarDiv">
        <KButton
          id="btnShowSearch"
          class="searchBarButton"
          :svg-icon="searchIcon"
          :fill-mode="'clear'"
          @click="searchClickedHandler($event)"
          data-testid="search-bar-button"
        ></KButton>
        <KTextBox
          id="txtSearch"
          :value="searchKeyword"
          :placeholder="t('labels.SearchPlaceHolder')"
          @change="searchKeywordChangedHandler($event)"
          @input="searchKeywordInputHandler($event)"
          data-testid="search-bar-input" />
      </div>
    </div>
    <Popup
      :anchor="'txtSearch'"
      :show="show"
      :popup-class="'aoe-search-bar-popup-content'"
      :popup-align="{ horizontal: 'left', vertical: 'top' }"
      data-testid="search-bar-popup"
    >
      <div class="prodSearch row mt-3" v-if="viewModels.length > 0" data-testid="search-bar-results">
        <div
          class="col-12 col-md-6 border"
          v-for="item in viewModels"
          :key="item.skuCode"
          :data-testid="`search-bar-result-item-${item.skuCode}`"
        >
          <div class="row p-2">
            <div class="col-4 imageDiv">
              <a
                :href="item.fullProductUrl"
                :data-category-code="item.product.productCode"
                :data-testid="`search-bar-product-link-${item.skuCode}`"
              >
                <img
                  :src="item.fullProductImageUrl"
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"
                  :data-testid="`search-bar-product-image-${item.skuCode}`"
                />
              </a>
            </div>
            <div class="col-8 prodSearchProdDetails p-2">
              <div class="row prodSearchProdRow">
                <div class="col-10 prodName p-2" :data-testid="`search-bar-product-name-${item.skuCode}`">{{ item.skuName }}</div>
                <div class="col-2 searchBarWishlistBtn p-2">
                  <AoeAddToWishlistButton
                    :skucode="item.skuCode"
                    :data-testid="`search-bar-wishlist-btn-${item.skuCode}`"
                  ></AoeAddToWishlistButton>
                </div>
              </div>
              <div class="row divProdRating">
                <div class="col-12">
                  <AoeProductReviewStars
                    :productcode="item.product.productCode"
                    :usecolor="false"
                    :showtotal="true"
                    starsize="medium"
                    :data-testid="`search-bar-product-rating-${item.skuCode}`">
                  </AoeProductReviewStars>
                </div>
              </div>
              <div class="row divProdPrice">
                <div class="col-4 prodPrice p-2" :data-testid="`search-bar-product-price-${item.skuCode}`">
                  {{ FormatCurrency(item.retailPrice, null) }}
                </div>
                <div class="col-8 prodSearchAddToCartButton">
                  <AoeAddToCartButton
                    :skucode="item.skuCode"
                    :availablequantity="item.calculatedAvailableQty.toString()"
                    :displayquantityspinner="'false'"
                    :data-testid="`search-bar-add-to-cart-btn-${item.skuCode}`"
                  ></AoeAddToCartButton>
                </div>
              </div>
              <div class="row divPricePerUnit">
                <div class="col-12">
                  <PricePerUnitWeight
                    :RetailPrice="item.retailPrice"
                    :SalePrice="null"
                    :IProductAttributes="item.product.attributes"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row" v-if="moreSearchResultsMessage" data-testid="search-bar-more-results-section">
          <div class="mx-auto moreSearchResults text-center">
            <KButton
              :fill-mode="'link'"
              @click="showMoreSearchClickedHandler($event)"
              data-testid="search-bar-show-more-btn"
              >{{ moreSearchResultsMessage }}</KButton
            >
          </div>
        </div>
      </div>
    </Popup>
  </div>
</template>
<style lang="scss">
.aoe-search-bar-popup-content {
  background-color: white;
  border: 1px solid black;
  padding: $spacing-2-5;
  width: 66%;
}

.aoe-search-bar-popup-content .prodImage {
  width: 100px;
  height: 100px;
}

.aoe-search-bar-popup-content .moreSearchResults {
  width: 150px;
}

.searchBarButton {
  position: absolute;
  top: 5px;
  left: 20px;
  transform: scale(1.5);
}

.searchBarDiv .k-input-inner {
  padding: $spacing-2-5;
  padding-left: 50px;
}

.searchBarWishlistBtn .btnwishListHeart {
  border: solid 1px black !important;
  border-radius: 50%;
  width: 40px;
  height: 40px;
}

.searchBarWishlistBtn svg {
  transform: scale(2)
}

.searchBarWishlistBtn .k-icon
{
  position: relative;
  top: 1px;
  left: 3px;
}

</style>
